<template>
  <a-modal v-model:open="computedShowModal"
           width="600px"
           :closable="false"
           :footer="null"
           :mask-closable="false"
           :mask-style="{backdropFilter: 'blur(2px)'}"
           :keyboard="false"
  >
    <div class="modal-content-body">
      <div class="search-client-header">
        <div class="modal-title">
          거래처 검색
        </div>
        <button
            type="button"
            class="btn-close"
            @click="uiHandler.closeModal"
        >
          <i class="fa-regular fa-x"/>
        </button>
      </div>
      <div class="search-client-content d-flex justify-content-center flex-column align-items-center">
        <!-- 지역 필터: 동명 치과가 많아 시/도·시/군/구로 좁힌다(덴티움 고객지원팀 요청, 2026-06-04) -->
        <div class="region-filter-container">
          <a-select
              v-model:value="searchParam.addrOne"
              :options="computedAddrOneOptions"
              placeholder="시/도 전체"
              allow-clear
              @change="uiHandler.changeAddrOne"
          />
          <a-select
              v-model:value="searchParam.addrTwo"
              :options="computedAddrTwoOptions"
              placeholder="시/군/구 전체"
              allow-clear
              show-search
              option-filter-prop="label"
              :disabled="computedAddrTwoOptions.length <= 1"
              @change="uiHandler.search"
          />
        </div>
        <div class="search-input-container">
          <i class="fa-solid fa-magnifying-glass"/>
          <input
              type="text"
              @keyup.enter="uiHandler.search"
              v-model="searchParam.condition"
              placeholder="거래처명·대표자명·사업자번호 (예: 남양주 연치과)"
          >
          <button class="search" @click="uiHandler.search">검색</button>
        </div>
        <div class="list-container">
          <div class="list-items" v-if="result.list.length > 0">
            <div
                v-for="(item, index) in result.list"
                class="list-item"
                :class="item.isSelected ? 'selected' : ''"
                @click="uiHandler.chooseItem(index)"
            >
              <a-typography-text :ellipsis="true" style="width: 100%;">
                <div class="item-left">
                  {{ item.name }}<span class="cno">({{ item.convertedHospitalInfo }})</span>
                </div>
              </a-typography-text>
              <div class="item-right" v-if="item.isSelected">
                <i class="fa-solid fa-check"/>
              </div>
            </div>
          </div>
          <div
              v-else
              style="
                align-items: center;
                justify-content: center;
                display: flex;
                margin-top: 164px;"
          >
            <no-result
                :colspan="10"
                :message="computedNoResultMessage"
                :height="'auto'"
                :noIcon="true"
            />
          </div>
          <div
              v-if="$store.state.isLoading !== false || result.list.length === 0"
              class="page-wrapper"
          />
          <pagination
              v-if="$store.state.isLoading === false && result.list.length > 0"
              :page-info="paginationData"
              page-name="SearchClientModal"
              :pagePerBlock="5"
              @change-page="uiHandler.paginationHandler"
          />
        </div>
      </div>
    </div>
    <div class="expiration-modal-footer">
      <button
          class="contract-cancel-btn btn-hover"
          @click="uiHandler.closeModal"
      >
        취소
      </button>
      <button
          class="contract-submit-btn btn-hover"
          @click="uiHandler.emitClient"
      >
        선택한 거래처 적용하기
      </button>
    </div>
  </a-modal>
</template>
<script>
import {
  logging,
  oneBtnDialog,
  businessNumberFormatter, jwtDecoder,
} from '@/utils/utils';
import NoResult from '@/views/components/NoResult';
import Pagination from '@/views/components/Pagination';
import {EmitEvents} from "@/views/ContractManagement/contracts/dentium/EmitEvents";
import {localStorageEnums} from "@/enums/localStorage.enums";
import regionTree from '@/assets/locations.json';

const toOptions = (names) => names.map((name) => ({label: name, value: name}));

export default {
  components: {
    Pagination,
    NoResult,
  },
  emits: Object.keys({...EmitEvents.ClientModal, ...EmitEvents.Common}),
  props: {
    showModal: {
      type: Boolean,
      required: true,
      default: false
    },
    initDataLoad: {
      type: Boolean,
      required: false,
      default: true
    },
    searchType: {
      type: String,
      required: false,
      default: 'HOSPITAL'
    }
  },
  computed: {
    computedShowModal() {
      return this.showModal;
    },
    computedNoResultMessage() {
      return this.$i18n.t('noresult_search');
    },
    computedAddrOneOptions() {
      return toOptions(Object.keys(regionTree));
    },
    computedAddrTwoOptions() {
      const districts = regionTree[this.searchParam.addrOne];
      return districts ? toOptions(Object.keys(districts)) : [];
    },
    formToParam() {
      const {searchParam} = this
      const obj = {};
      obj.page = this.paginationData.currentPage;
      obj.limit = 10;
      obj.type = this.searchType;
      // 검색어·지역 둘 다 없을 때만 담당 직원 병원으로 좁힌다(덴티움). 지역만 골라도 전체 병원에서 찾는다.
      if (this.searchType === 'HOSPITAL' && !searchParam.condition && !searchParam.addrOne) {
        const data = JSON.parse(localStorage.getItem(localStorageEnums.CompanyInfo));
        obj.username = data?.accountInfo?.username;
      }
      if (searchParam.addrOne) obj.addrOne = searchParam.addrOne;
      if (searchParam.addrOne && searchParam.addrTwo) obj.addrTwo = searchParam.addrTwo;
      if (searchParam?.condition) {
        if (typeof searchParam.condition === "string") {
          obj.search = 'name'
        }
        if (typeof searchParam.condition === "number") {
          obj.search = 'cNo'
        }
        obj.condition = searchParam.condition;
      }
      return obj
    }
  },
  data() {
    return {
      businessNumberFormatter,
      uiHandler: this.ui(),
      apiHandler: this.api(),
      searchParam: {
        search: '', // 숫자일때: cNo(사업자 번호), 문자일때: name(병원명)
        condition: '',
        username: '',
        addrOne: undefined, // 시/도 (undefined = 전체, a-select placeholder 표시용)
        addrTwo: undefined, // 시/군/구
      },
      paginationData: {
        listSize: 0,
        rowsPerPage: 10,
        currentPage: 1,
        totalPages: 0,
      },
      selectedItem: {},
      result: {
        list: [],
        totalDocs: 0,
      },
    }
  },
  mounted() {
    if (this.initDataLoad) {
      this.uiHandler.getClientList()
    }
  },
  methods: {
    ui() {
      const paginationHandler = async (childPassParam) => {
        this.paginationData.currentPage = childPassParam.page;
        await this.uiHandler.getClientList();
      }
      const joinHospitalInfo = (item) => {
        const array = [
          item.ceo,
          businessNumberFormatter(item.cNo),
        ];

        let address = '';
        if (item.bizAddr) {
          address = item.bizAddr;
        } else if (item.addrOne && item.addrTwo && item.addrThree) {
          address = `${item.addrOne} ${item.addrTwo} ${item.addrThree}`;
        }

        if (item.addrDetail) address += ` ${item.addrDetail}`;

        if (address) array.push(address)

        return array.join(', ');
      }

      const chooseItem = (index) => {
        if (this.result.list[index].isSelected) return;
        this.result.list.forEach((item, i) => {
          if (index !== i) item.isSelected = false;
        });
        this.result.list[index].isSelected = true;
        this.selectedItem = this.result.list[index];
      }
      const search = async () => {
        this.paginationData.currentPage = 1;
        await this.uiHandler.getClientList();
      }
      // 시/도를 바꾸면 하위 시/군/구 선택은 무효라 비우고 바로 다시 찾는다.
      const changeAddrOne = async () => {
        this.searchParam.addrTwo = undefined;
        await search();
      }
      const validateData = async () => {
        const selectedItem = this.selectedItem;
        if (!selectedItem.isSelected) {
          await oneBtnDialog('거래처 미선택', '거래처를 선택해 주세요.', false);
          return {status: false};
        }

        return {status: true, selectedItem};
      }
      return {
        getClientList: async () => {
          await this.apiHandler.getClientList(this.formToParam, async (response) => {
            if (response.data.message) {
              await oneBtnDialog('검색어 오류', '검색어에 특수문자를 포함할 수 없습니다.', false);
              return;
            }

            console.log("1 1231231==>", response.data.docs)

            if (jwtDecoder(localStorage.getItem('accessTokenManu')) === process.env.VUE_APP_POINT_IMPLANT) { // todo 임시코드
              console.log("2 1231231==>", response.data.docs)
              this.result.list = response.data.docs.filter(l => l.name === '서티치과');
            } else {
              console.log("3 1231231==>", response.data.docs)
              this.result.list = response.data.docs;
            }

            this.result.list = response.data.docs;
            this.result.totalDocs = response.data.totalDocs;
            this.paginationData.totalPages = response.data.totalPages;
            for (let i = 0; i < this.result.list.length; i++) {
              const item = this.result.list[i];
              item.convertedHospitalInfo = joinHospitalInfo(item);
              item.isSelected = false;
            }
          }, async (error) => {
            await oneBtnDialog(this.$i18n.t('hospital_list_err_title'), this.$i18n.t('hospital_list_err_content'), false);
          })
        },
        chooseItem,
        paginationHandler,
        emitClient: async () => {
          const result = await validateData();
          if (!result.status) return;
          await this.$emit(EmitEvents.ClientModal.CompleteSearch, result.selectedItem);
        },
        search,
        changeAddrOne,
        closeModal: () => {
          this.$emit(EmitEvents.Common.CloseModal, false);
        }
      }
    },
    api() {
      return {
        getClientList: async (params, success, fail) => {
          this.$store.commit('setIsLoading', true);
          return this.$apiInstance.post(`/companies/list/contract/v2`, params)
              .then((response) => {
                success(response);
              }, (error) => {
                console.log(error);
                fail(error);
              }).finally(() => this.$store.commit('setIsLoading', false));
        },
      }
    },
    /*
      달력 패키지로 생일 선택 시 포맷팅
    */

  }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/search-client-modal.scss';

.region-filter-container {
  display: flex;
  gap: 8px;
  width: 100%;

  .ant-select {
    flex: 1;
    min-width: 0;
  }

  // 아래 검색창(40px)과 높이를 맞춘다.
  :deep(.ant-select-selector) {
    height: 40px !important;
    align-items: center;
    border-radius: 8px !important;
  }
}
</style>
